import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchAdminPerformanceOverview,
  sanitizeClientErrorMessage,
} from "../../../api/adminData";

/**
 * The single read behind the Performance tab.
 *
 * ONE REQUEST, TWO PERIODS
 * ------------------------
 * `/dashboard/performance` answers for the selected period AND derives its
 * comparison period server-side, inside one database snapshot. So a
 * period-over-period comparison costs no second call, and — more importantly —
 * the two cannot come from two different states of the world, which is what a
 * second request would allow.
 *
 * THE RANGE IS NO LONGER COLLAPSED (P2)
 * -------------------------------------
 * This file used to reduce a multi-month range to its TO-month and say so with
 * "· Stand <Monat>", because the endpoint took one month and nothing defined a
 * multi-month operating margin. P1 settled that: the endpoint now takes an
 * inclusive window and folds the same monthly truth over it. So a range is sent
 * as a range, in ONE request.
 *
 * There is still NO aggregation in this file, and there must never be: summing
 * months in the browser would be a second KPI truth beside the one the backend
 * publishes, and it is the specific failure this whole programme exists to end.
 * A six-month period is one request, not six.
 *
 * "GESAMTE ZEIT" IS REFUSED, NOT FAKED
 * ------------------------------------
 * The contract bounds a period at 24 months, and an unbounded operating margin
 * has no defined denominator. Falling back to the to-month would answer a
 * question nobody asked — which is exactly what the pre-P2 collapse did — and
 * quietly showing "the last 24 months" would put a window on screen that the
 * label does not name. The tab states that the period is unsupported instead.
 *
 * INERT UNTIL THE TAB IS ACTUALLY OPEN
 * ------------------------------------
 * `AdminUebersichtPage` mounts all five panels at once. Ungated, this hook would
 * issue a Performance request at every page load. Nothing happens until
 * `enabled` is true.
 */

const EMPTY = { data: null, error: "" };

/**
 * The request this period asks for, or `null` when the period has no answer.
 *
 * Returns the wire parameters directly rather than an intermediate shape: the
 * two forms are mutually exclusive on the contract, and keeping them mutually
 * exclusive HERE means no later code can accidentally send both.
 *
 *   mode "month"  -> { year, month }                     (unchanged, backward compatible)
 *   mode "custom" -> { from_year, from_month, to_year, to_month }
 *   mode "all"    -> null                                (unsupported, see above)
 *
 * STILL NO YTD HEURISTIC — now because it is unnecessary rather than because it
 * was refused. A `{mode:"custom", from: Jan, to: Sep}` period may be the "Dieses
 * Jahr" preset or a hand-picked window, and the DATES cannot tell them apart.
 * Before P4 the picker discarded which preset produced a period, so this file had
 * no honest way to special-case YTD and sent no explicit comparison at all. The
 * period now carries `presetKey`, so the comparison follows the user's stated
 * intent — see `selectPerformanceComparison` — and `from.month === 1` still
 * appears nowhere in this file or any other.
 */
export function selectPerformanceRequest(period) {
  const mode = period?.mode;
  const to = period?.to;
  const from = period?.from;

  if (mode === "all") return null;

  if (mode === "custom" && from?.year != null && from?.month != null) {
    if (to?.year == null || to?.month == null) return null;
    return {
      from_year: Number(from.year),
      from_month: Number(from.month),
      to_year: Number(to.year),
      to_month: Number(to.month),
      // Spread last so a period with no special comparison adds no keys at all
      // and the request stays byte-identical to the pre-P4 one.
      ...selectPerformanceComparison(period),
    };
  }

  // mode "month" (and any period that names only one bound): the single-month
  // call the endpoint has always taken.
  const single = to || from;
  if (!single || single.year == null || single.month == null) return null;
  return { year: Number(single.year), month: Number(single.month) };
}

/**
 * The explicit comparison window a period asks for, or `{}` for the backend default.
 *
 * WHICH PERIODS GET ONE, AND WHY ONLY THOSE
 * -----------------------------------------
 * Without `comparison_*` the endpoint compares against the equal-length window
 * immediately before the selection. That is the right answer almost everywhere:
 * a single month wants the month before, "Letzte 6 Monate" wants the six months
 * before, and a range somebody drew by hand has no other defensible reference.
 *
 * It is the WRONG answer for exactly two presets, because both name a window
 * whose meaning is a CALENDAR position rather than a length:
 *
 *   ytd       Jan–Sep 2026 → the equal-length window before it is Apr–Dec 2025,
 *             nine months that answer no question anyone asked. "Dieses Jahr"
 *             is only meaningful against the same stretch of the year before:
 *             Jan–Sep 2025.
 *   prevYear  Jan–Dec 2025 → the calendar year before it, Jan–Dec 2024. The
 *             default happens to land there too, and it is sent explicitly
 *             anyway so the comparison is a property of the selection rather
 *             than of a default that could reasonably change.
 *
 * Both are therefore the same arithmetic — the selection, shifted back twelve
 * months — and both are keyed off the PRESET, never off the dates. A hand-picked
 * Jan–Sep 2026 has identical bounds to the YTD preset and deliberately gets no
 * explicit comparison: the user drew a window, not a year-to-date.
 *
 * Returns `{}` rather than `null` so callers can spread it unconditionally and a
 * period with no special rule adds no keys to the request at all.
 */
export function selectPerformanceComparison(period) {
  const presetKey = period?.presetKey;
  if (presetKey !== "ytd" && presetKey !== "prevYear") return {};

  const from = period?.from;
  const to = period?.to;
  if (from?.year == null || from?.month == null) return {};
  if (to?.year == null || to?.month == null) return {};

  return {
    comparison_from_year: Number(from.year) - 1,
    comparison_from_month: Number(from.month),
    comparison_to_year: Number(to.year) - 1,
    comparison_to_month: Number(to.month),
  };
}

/**
 * A resolved request as a stable key — used to discard a previous period's response.
 *
 * P2 made this name the WHOLE window. It used to name only a month, so
 * "Apr–Sep" and "Sep alone" produced the same key: harmless while the tab
 * genuinely answered for one month, and a stale-response bug the moment the
 * request carries a range.
 */
export function performanceRequestKey(request) {
  if (!request) return "";
  const pad = (n) => String(n).padStart(2, "0");
  if (request.year != null) {
    return `m:${request.year}-${pad(request.month)}`;
  }
  const selected =
    `r:${request.from_year}-${pad(request.from_month)}` +
    `..${request.to_year}-${pad(request.to_month)}`;
  // P4: the comparison is part of the key, because it is part of the request.
  // "Dieses Jahr" and a hand-picked Januar–September have the SAME selected
  // bounds and different comparisons, so a key naming only the selection would
  // call the two identical — and switching between them would keep the first
  // one's response on screen while the second one's numbers were never fetched.
  if (request.comparison_from_year == null) return selected;
  return (
    `${selected}|c:${request.comparison_from_year}-${pad(request.comparison_from_month)}` +
    `..${request.comparison_to_year}-${pad(request.comparison_to_month)}`
  );
}

export function usePerformanceData(period, { enabled = true } = {}) {
  const request = useMemo(() => selectPerformanceRequest(period), [period]);
  // A primitive, so the effect below re-runs on a real period change and not on
  // every render that rebuilds an equal object.
  const requestKey = performanceRequestKey(request);

  const [state, setState] = useState(EMPTY);
  // The request `state` actually describes — "" while nothing has landed.
  // `loading` is DERIVED from it rather than stored: a stored flag is always one
  // render late, because effects run after paint, so the very frame in which the
  // tab becomes active would paint em-dashes with `loading` still false.
  const [settledKey, setSettledKey] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  // The request the newest fetch belongs to. A ref, not state: an in-flight
  // promise closed over an OLDER period, and comparing its captured values with
  // themselves would always agree.
  const activeKeyRef = useRef("");

  useEffect(() => {
    if (!enabled || !requestKey) {
      // Nothing to show and nothing in flight — NOT "still loading", which would
      // leave a closed tab, or an unsupported period, under skeletons forever.
      activeKeyRef.current = "";
      setState(EMPTY);
      setSettledKey("");
      return undefined;
    }

    let cancelled = false;
    activeKeyRef.current = requestKey;
    // Nothing in hand for the request about to go out — including a retry of a
    // period that HAD settled, which must show its skeletons again rather than
    // leave stale figures standing as if they were fresh.
    setSettledKey("");

    fetchAdminPerformanceOverview(request)
      .then(
        (data) => ({ data, error: "" }),
        (e) => ({
          data: null,
          error: sanitizeClientErrorMessage(
            e?.message,
            "Performance-Daten konnten nicht geladen werden."
          ),
        })
      )
      .then((result) => {
        if (cancelled) return;
        // A response for a period the user has already left is discarded, not
        // rendered: React runs cleanup for the previous period, but a slow
        // Apr–Sep request and a fast September one can still resolve out of
        // order and whichever lands LAST would win.
        if (activeKeyRef.current !== requestKey) return;
        setState(result);
        setSettledKey(requestKey);
      });

    return () => {
      cancelled = true;
    };
    // `request` is derived from `requestKey`; depending on the key keeps the
    // effect from re-firing on an equal-but-new object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, requestKey, reloadToken]);

  // Retry re-runs the effect for whatever period is selected NOW — never for the
  // period that happened to fail, which may no longer be the one on screen.
  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  // There is a period to answer for, and what we hold is not yet its answer.
  // False when the tab is closed or the period is unsupported, so skeletons
  // never stand over a panel that will not be filled.
  const loading = Boolean(enabled && requestKey) && settledKey !== requestKey;

  return {
    data: state.data,
    error: state.error,
    loading,
    reload,
    request,
    /** The period carries no answerable Performance window ("Gesamte Zeit"). */
    unsupported: Boolean(period) && request === null,
  };
}
